import React, { CSSProperties, useRef, useState, forwardRef, useImperativeHandle, useEffect } from 'react';
/** TODO:
 * We use this fork untill react-quill is up to date
 * https://github.com/zenoamaro/react-quill/pull/973
 */
import ReactQuill from '@pending-forks/react-quill';
import '@pending-forks/react-quill/dist/quill.snow.css';

import EditorToolbar, { modules, formats } from './editorToolbar';
import './style.css';

type Props = {
  value: string;
  onChange: ({
    content,
    textWithoutHtml,
    semanticHtml,
  }: {
    content: string;
    textWithoutHtml?: string;
    semanticHtml?: string;
  }) => void;
  style?: CSSProperties;
  toolbarId?: string;
  onFocus?: () => void;
};

const saveSpacesAndTabs = (value: string) => {
  return value.replace(/ {2,}/g, (match) => '&nbsp;'.repeat(match.length)).replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;');
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Editor = forwardRef(({ value, onChange, style, toolbarId, onFocus }: Props, ref: any) => {
  const [internalValue, setInternalValue] = useState(value);
  const [indexCursor, setIndexCursor] = useState<number>(0);

  const reactQuillRef = useRef<ReactQuill>(null);
  const isTypingRef = useRef(false);

  useEffect(() => {
    if (!isTypingRef.current) {
      setInternalValue(saveSpacesAndTabs(value));
    }
  }, [value]);

  const handleChange = (content: string) => {
    setInternalValue(content);

    if (typeof reactQuillRef.current?.getEditor === 'function') {
      const editor = reactQuillRef.current?.getEditor();
      const textWithoutHtml = editor.getText();
      const semanticHtml = `<div class="ql-editor">${content}</div>`;
      onChange({ content, textWithoutHtml, semanticHtml });
    } else {
      onChange({ content });
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reactQuillRef.current?.getEditor().on('editor-change', (eventName: any, ...args: any) => {
    if (eventName === 'text-change') {
      setIndexCursor(args[0].ops[0].retain ? args[0].ops[0].retain + 1 : 0);
    } else if (eventName === 'selection-change' && args[0]) {
      setIndexCursor(args[0].index);
    }
  });

  useImperativeHandle(ref, () => ({
    insertText(text: string) {
      const editor = reactQuillRef?.current?.getEditor();
      if (!Number.isNaN(indexCursor) && editor) {
        const semanticHtml = `<div class="ql-editor">${text}</div>`;
        editor.clipboard.dangerouslyPasteHTML(indexCursor, saveSpacesAndTabs(semanticHtml));
      }
    },
  }));

  return (
    <div style={{ background: 'white' }}>
      <EditorToolbar id={toolbarId} />
      <ReactQuill
        onFocus={() => {
          onFocus?.();
          isTypingRef.current = true;
        }}
        onBlur={() => {
          isTypingRef.current = false;
        }}
        ref={reactQuillRef}
        theme="snow"
        value={internalValue}
        onChange={handleChange}
        placeholder=""
        modules={modules(toolbarId)}
        formats={formats}
        style={style}
      />
    </div>
  );
});

Editor.displayName = 'Editor';

export default Editor;

// TODO Follow Issue: https://github.com/slab/quill/issues/979
// TODO: FYI: https://github.com/slab/quill/issues/979#issuecomment-690318932
